import { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery } from "@tanstack/react-query";

import type { DashboardParameter } from "../../types";
import { COMPONENT_HEIGHTS } from "../../types/tokens";
import {
  buildPersesEsqlRequest,
  createPersesEsqlDatasource,
} from "../../services/perses/esqlDatasource";
import type { useConnectionStore } from "../../store/useConnectionStore";

import type { ParameterValue } from "./parameterUtils";
import { parseParameterValue, formatValueForInput } from "./parameterUtils";

interface ParameterControlProps {
  param: DashboardParameter;
  activeProfileId: string | null;
  connection: ReturnType<typeof useConnectionStore.getState>["connection"];
  parameters: DashboardParameter[];
  onChange: (value: DashboardParameter["value"]) => void;
  onEdit: () => void;
  onDelete: () => void;
}

export default function ParameterControl({
  param,
  activeProfileId,
  connection,
  parameters,
  onChange,
  onEdit,
  onDelete,
}: ParameterControlProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const hasQueryableEsqlSource =
    param.source.mode === "esql" && Boolean(param.source.query.trim()) && Boolean(connection);

  // Fetch options from ES|QL when source mode is esql
  const esqlQuery = param.source.mode === "esql" ? param.source.query : "";

  // Only include parameters actually referenced by {{name}} tokens in the
  // query key so that unrelated variable changes don't invalidate the cache.
  const referencedParams = useMemo(() => {
    if (!esqlQuery) return [];
    const tokens = new Set<string>();
    for (const m of esqlQuery.matchAll(/\{\{(\w+)\}\}/g)) {
      if (m[1]) tokens.add(m[1]);
    }
    if (tokens.size === 0) return [];
    return parameters
      .filter((p) => tokens.has(p.name))
      .map((p) => ({ name: p.name, type: p.type, value: p.value }));
  }, [esqlQuery, parameters]);

  const { data: esqlOptions = [] } = useQuery<string[]>({
    queryKey: ["parameter-options", activeProfileId, connection?.url, esqlQuery, referencedParams],
    queryFn: async ({ signal }) => {
      const datasource = createPersesEsqlDatasource(connection!);
      const request = buildPersesEsqlRequest(esqlQuery, { parameters });
      const result = await datasource.execute(request, signal);
      return result.values?.map((row) => String(row[0] ?? "")).filter(Boolean) ?? [];
    },
    enabled: hasQueryableEsqlSource,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const optionStrings =
    param.source.mode === "options"
      ? param.source.values
      : param.source.mode === "esql"
        ? hasQueryableEsqlSource
          ? esqlOptions
          : []
        : [];
  const options = Array.from(new Set(optionStrings))
    .map((opt) => ({ label: opt, parsed: parseParameterValue(param.type, opt) }))
    .filter((entry) => entry.parsed.value !== undefined)
    .map((entry) => ({ label: entry.label, value: entry.parsed.value as ParameterValue }));
  const currentValueInput = formatValueForInput(param.type, param.value);
  const [draftInput, setDraftInput] = useState(currentValueInput);
  const [prevParamValue, setPrevParamValue] = useState(param.value);
  if (param.value !== prevParamValue) {
    setPrevParamValue(param.value);
    setDraftInput(currentValueInput);
    setValidationError(null);
  }

  const commitDraftValue = useCallback(() => {
    const parsed = parseParameterValue(param.type, draftInput);
    if (parsed.value === undefined) {
      setValidationError(parsed.error ?? "Invalid value");
      return;
    }
    setValidationError(null);
    onChange(parsed.value);
    setDraftInput(formatValueForInput(param.type, parsed.value));
  }, [param.type, draftInput, onChange]);

  return (
    <Box
      data-testid={`parameter-row-${param.name}`}
      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {param.label || param.name}:
      </Typography>

      {options.length > 0 || param.type === "boolean" ? (
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={currentValueInput}
            onChange={(e) => {
              const parsed = parseParameterValue(param.type, e.target.value);
              if (parsed.value === undefined) {
                setValidationError(parsed.error ?? "Invalid value");
                return;
              }
              setValidationError(null);
              onChange(parsed.value);
            }}
            displayEmpty
            sx={{ height: COMPONENT_HEIGHTS.input, fontSize: "0.75rem" }}
          >
            {currentValueInput &&
              !options.some(
                (opt) => formatValueForInput(param.type, opt.value) === currentValueInput,
              ) && (
                <MenuItem key={`param-value-${param.name}`} value={currentValueInput}>
                  {currentValueInput}
                </MenuItem>
              )}
            {param.type === "boolean" && options.length === 0 && (
              <>
                <MenuItem value="true">true</MenuItem>
                <MenuItem value="false">false</MenuItem>
              </>
            )}
            {options.map((opt) => (
              <MenuItem
                key={`option-${opt.label}-${String(opt.value)}`}
                value={formatValueForInput(param.type, opt.value)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          size="small"
          value={draftInput}
          onChange={(e) => {
            const nextInput = e.target.value;
            setDraftInput(nextInput);
            const parsed = parseParameterValue(param.type, nextInput);
            setValidationError(
              parsed.value === undefined ? (parsed.error ?? "Invalid value") : null,
            );
          }}
          onBlur={commitDraftValue}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitDraftValue();
            }
          }}
          error={Boolean(validationError)}
          helperText={validationError}
          sx={{
            width: 120,
            "& .MuiInputBase-input": { py: 0.5, px: 1, fontSize: "0.75rem" },
          }}
        />
      )}

      <Tooltip title="Edit variable">
        <IconButton size="small" onClick={onEdit} sx={{ p: 0.5 }} aria-label="Edit variable">
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove variable">
        <IconButton size="small" onClick={onDelete} sx={{ p: 0.5 }} aria-label="Remove variable">
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
